"use client";

import Image from "next/image";
import { Link } from "@/navigation";
import { useTranslations } from "next-intl";
import PartsCarousel from "@/components/NewVersion/service/PartsCarousel";

const GlobeIcon = ({ className }: { className?: string }) => (
	<svg
		className={`h-14 w-14 sm:h-16 sm:w-16 md:h-[72px] md:w-[72px] lg:h-20 lg:w-20 ${
			className ?? "text-[#0d68ea]"
		}`}
		viewBox="0 0 48 48"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
	>
		<circle cx="24" cy="24" r="20" />
		<ellipse cx="24" cy="24" rx="20" ry="8" />
		<ellipse cx="24" cy="24" rx="8" ry="20" />
	</svg>
);

const CheckIcon = () => (
	<svg
		className="inline-block h-[1em] w-[1em] flex-shrink-0 align-middle"
		viewBox="0 0 20 20"
		fill="none"
		aria-hidden="true"
	>
		<circle cx="10" cy="10" r="10" fill="#64a525" />
		<path
			d="M6 10.5L8.5 13L14 7.5"
			stroke="white"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
);

const FEATURE_KEYS = [
	{ titleKey: "feature1Title", descKey: "feature1Desc" },
	{ titleKey: "feature2Title", descKey: "feature2Desc" },
	{ titleKey: "feature3Title", descKey: "feature3Desc" },
] as const;

const VS_FEATURE_KEYS = [
	{ titleKey: "vs5axisTitle", descKey: "vs5axisDesc" },
	{ titleKey: "vs3axisTitle", descKey: "vs3axisDesc" },
] as const;

const DISPLAY_IMAGES = [
	"/image/new-version/machining/five-axis/banner/dise/banner-1.webp",
	"/image/new-version/machining/five-axis/banner/dise/banner-2.webp",
	"/image/new-version/machining/five-axis/banner/dise/banner-3.webp",
	"/image/new-version/machining/five-axis/banner/dise/banner-4.webp",
	"/image/new-version/machining/five-axis/banner/dise/banner-5.webp",
];

export default function FiveAxisIntro() {
	const t = useTranslations("fiveAxisIntro");

	return (
		<section className="bg-white py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				{/* 标题行：文字 + Globe 图标 */}
				<div className="relative mb-8 sm:mb-10 md:mb-12">
					<h2 className="flex flex-col gap-1 text-2xl font-bold leading-tight text-black sm:mb-5 sm:text-[30px] md:mb-6 md:text-4xl">
						<span>{t("title1")}</span>
						<span>{t("title2")}</span>
					</h2>
					<div className="absolute left-[45%] top-1/2 -translate-x-1/2 -translate-y-1/2">
						<GlobeIcon className="!text-themePrimary400" />
					</div>
				</div>

				{/* 图片 + 特性并排 */}
				<div className="flex flex-col gap-8 lg:flex-row lg:items-stretch lg:gap-12 xl:gap-16">
					{/* 左侧图片 */}
					<div className="w-full lg:w-[45%]">
						<div className="relative aspect-[4/3] w-full overflow-hidden shadow-lg lg:aspect-auto lg:h-full lg:min-h-[480px]">
							<Image
								src="/image/new-version/machining/five-axis/intro/5-axis-machining-with-js-precision.webp"
								alt="5-Axis CNC Machining With JS Precision"
								fill
								className="object-cover"
								sizes="(max-width: 1024px) 100vw, 45vw"
							/>
						</div>
					</div>

					{/* 右侧特性列表 + 按钮 */}
					<div className="flex w-full flex-col justify-center lg:w-[55%]">
						<ul className="mb-8 space-y-5">
							{FEATURE_KEYS.map((feature, index) => (
								<li key={index} className="text-sm leading-relaxed sm:text-base">
									<p className="mb-4">
										<CheckIcon />{" "}
										<span className="ml-4 text-base font-semibold text-black sm:text-lg">
											{t(feature.titleKey)}
										</span>
									</p>
									<p className="text-sm leading-relaxed text-black md:text-base">{t(feature.descKey)}</p>
								</li>
							))}
						</ul>
						<Link
							href="/upload"
							className="group inline-flex w-fit items-center justify-center gap-2 rounded bg-themePrimary400 px-4 py-2.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-opacity-80 hover:text-white sm:px-5 sm:py-3 md:px-6 md:py-3.5 md:text-base"
						>
							{t("getQuote")}
						</Link>
					</div>
				</div>

				{/* 下方扩展区：What is + 5-axis vs 3-axis */}
				<div className="mb-14 max-md:mb-10">
					<div className="my-12 grid gap-6 md:my-24 lg:grid-cols-[1.1fr_1.4fr] lg:items-center">
						<h3 className="text-lg font-bold text-black sm:text-xl md:text-2xl">{t("whatIsTitle")}</h3>
						<p className="text-sm leading-relaxed text-black md:text-base">{t("whatIsDesc")}</p>
					</div>

					<div className="grid gap-6 lg:grid-cols-[1fr_1.1fr] lg:items-start">
						<div>
							<h3 className="mb-6 text-lg font-bold text-black sm:text-xl md:text-2xl">{t("vsTitle")}</h3>
							<ul className="space-y-5">
								{VS_FEATURE_KEYS.map((feature, index) => (
									<li key={index} className="text-sm leading-relaxed sm:text-base">
										<p className="mb-4">
											<CheckIcon />{" "}
											<span className="ml-4 text-base font-semibold text-black sm:text-lg">
												{t(feature.titleKey)}
											</span>
										</p>
										<p className="text-sm leading-relaxed text-black md:text-base">{t(feature.descKey)}</p>
									</li>
								))}
								{/* 第三项：Selection Criteria 特殊处理 */}
								<li className="text-sm leading-relaxed sm:text-base">
									<p className="mb-4">
										<CheckIcon />{" "}
										<span className="ml-4 text-base font-semibold text-black sm:text-lg">
											{t("selectionTitle")}
										</span>
									</p>
									<p className="text-sm leading-relaxed text-black md:text-base">
										<strong>{t("choose3axis")}</strong> {t("choose3axisDesc")}
									</p>
									<p className="text-sm leading-relaxed text-black md:text-base">
										<strong>{t("choose5axis")}</strong> {t("choose5axisDesc")}
									</p>
								</li>
							</ul>
						</div>

						<div className="relative aspect-[4/3] w-full overflow-hidden">
							<Image
								src="/image/new-version/machining/five-axis/intro/5-axis-vs-3-axis-cnc-machining.webp"
								alt={t("vsImageAlt")}
								fill
								className="object-cover"
								sizes="(max-width: 1024px) 100vw, 50vw"
							/>
						</div>
					</div>
				</div>

				{/* 5 axis CNC Parts Display 轮播区 */}
				<PartsCarousel
					title={t("partsDisplayTitle")}
					images={DISPLAY_IMAGES}
					altPrefix={t("partsDisplayAlt")}
				/>
			</div>
		</section>
	);
}
